<template>
  <div class="asset-upload" :class="{ 'asset-upload--compact': compact }">
    <el-upload
      class="asset-uploader"
      action="#"
      :accept="accept"
      :show-file-list="false"
      :http-request="handleUpload"
      :before-upload="beforeUpload"
    >
      <div v-if="value" class="asset-preview">
        <video v-if="isVideo" :src="previewUrl" controls />
        <div v-else-if="isFile" class="file-preview">
          <i class="el-icon-document"></i>
          <span>{{ fileLabel }}</span>
        </div>
        <div v-else-if="previewFailed" class="preview-fallback">
          <i class="el-icon-picture-outline"></i>
          <span>暂无法预览</span>
        </div>
        <img v-else :src="previewUrl" alt="素材预览" @error="previewFailed = true">
        <div class="asset-mask">
          <i class="el-icon-upload2"></i>
          <span>替换素材</span>
        </div>
      </div>
      <div v-else class="upload-empty">
        <i class="el-icon-plus"></i>
        <span>{{ uploadLabel }}</span>
      </div>
    </el-upload>

    <el-progress v-if="uploading" :percentage="progress" :stroke-width="6" />

    <div class="asset-actions">
      <el-button v-if="value" size="mini" :type="compact ? 'text' : 'danger'" :plain="!compact" @click="removeAsset">
        {{ compact ? '移除图片' : '删除' }}
      </el-button>
    </div>
  </div>
</template>

<script>
import { uploadAsset } from "../api/resources";
import { apiOrigin } from "../api/config";

export default {
  name: "AssetUpload",
  props: {
    value: { type: String, default: "" },
    type: { type: String, default: "image" },
    category: { type: String, default: "content" },
    compact: { type: Boolean, default: false }
  },
  data() {
    return {
      uploading: false,
      progress: 0,
      previewFailed: false
    };
  },
  watch: {
    value() {
      this.previewFailed = false;
    }
  },
  computed: {
    accept() {
      if (this.type === "video") return "video/*";
      if (this.type === "file") return ".pdf,.doc,.docx,image/*";
      return "image/*";
    },
    isVideo() {
      return this.type === "video";
    },
    isFile() {
      return this.type === "file";
    },
    uploadLabel() {
      if (this.type === "video") return "上传视频";
      if (this.type === "file") return "上传文件";
      return "上传图片";
    },
    fileLabel() {
      return this.value ? this.value.split("/").pop() : "已选择文件";
    },
    previewUrl() {
      if (!this.value || /^https?:\/\//.test(this.value)) return this.value;
      if (this.value.indexOf("/uploads/") !== 0) return this.value;
      return `${apiOrigin()}${this.value}`;
    }
  },
  methods: {
    beforeUpload(file) {
      const matched = this.type === "video"
        ? file.type.indexOf("video/") === 0
        : this.type === "file"
          ? file.type.indexOf("image/") === 0 || [
            "application/pdf",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          ].includes(file.type)
          : file.type.indexOf("image/") === 0;
      if (!matched) this.$message.error(this.type === "video" ? "请选择视频文件" : this.type === "file" ? "请选择 PDF、Word 或图片文件" : "请选择图片文件");
      return matched;
    },
    async handleUpload(options) {
      this.uploading = true;
      this.progress = 12;
      try {
        const timer = window.setInterval(() => {
          if (this.progress < 88) this.progress += 8;
        }, 180);
        const res = await uploadAsset(options.file, {
          type: this.type,
          category: this.category
        });
        window.clearInterval(timer);
        this.progress = 100;
        const asset = res.data || {};
        this.previewFailed = false;
        this.$emit("input", asset.file_url || "");
        this.$emit("change", asset);
        this.$message.success("素材上传成功");
      } catch (error) {
        this.$message.error(error.message || "素材上传失败");
      } finally {
        window.setTimeout(() => {
          this.uploading = false;
          this.progress = 0;
        }, 400);
      }
    },
    removeAsset() {
      this.$confirm("确定移除当前表单中的文件吗？不会删除服务器上的已上传文件。", "删除确认", { type: "warning" })
        .then(() => {
          this.previewFailed = false;
          this.$emit("input", "");
          this.$emit("change", null);
        })
        .catch(() => {});
    }
  }
};
</script>

<style scoped>
.file-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 120px;
  color: #2458b8;
  background: #f5f8ff;
  border: 1px solid #d9e4ff;
}

.file-preview i {
  margin-bottom: 8px;
  font-size: 32px;
}

.file-preview span {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.preview-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  height: 100%;
  color: #909399;
  font-size: 12px;
  background: #f5f7fa;
}

.preview-fallback i {
  font-size: 24px;
}

.asset-upload--compact,
.asset-upload--compact .asset-uploader,
.asset-upload--compact .upload-empty,
.asset-upload--compact .asset-preview {
  max-width: 132px;
}

.asset-upload--compact .upload-empty,
.asset-upload--compact .asset-preview {
  height: 72px;
  border-radius: 6px;
}

.asset-upload--compact .file-preview {
  min-height: 0;
}

.asset-upload--compact .asset-actions {
  min-height: 20px;
  margin-top: 2px;
}

.asset-upload--compact .asset-actions .el-button {
  padding: 3px 0;
  font-size: 12px;
}
</style>
